// Load a CSV, say which column is which, look at what will be sent, send it.
//
// WHY A COLUMN MAPPER RATHER THAN A PARSER PER BANK. Nobody building this had ever
// seen the FNB export or the FuelEasy export. Writing a parser against a guessed
// column layout produces something that looks finished and is wrong the first time
// a real file arrives, and wrong here means a deposit silently unmatched or a date
// read eleven days out. The columns are guessed, the guess is shown, and a person
// confirms it before anything is sent. When the real files have been seen a few
// times the guesses can harden; the mapper stays, because bank exports change.
//
// The preview is not decoration. It shows the parsed value, not the raw cell, so a
// date read month-first or an amount read with the wrong decimal separator is
// visible before it reaches the database rather than after.
import { useMemo, useRef, useState } from 'react'
import { Upload, AlertCircle, CheckCircle2, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toSheet, parseAmount, parseDate, guessColumn, type Sheet } from './csv'

export type FieldSpec = {
  key: string
  label: string
  kind: 'date' | 'datetime' | 'amount' | 'number' | 'text'
  required?: boolean
  candidates: string[]
  help?: string
}

export type ImportDialogProps = {
  title: string
  // One sentence saying what this file proves and what happens if it is wrong.
  blurb: string
  fields: FieldSpec[]
  busy?: boolean
  result?: unknown
  error?: string | null
  onCancel: () => void
  onSubmit: (rows: Record<string, string | number | null>[], fileName: string) => void
}

function coerce(kind: FieldSpec['kind'], raw: string): string | number | null {
  if (kind === 'amount' || kind === 'number') return parseAmount(raw)
  if (kind === 'date') return parseDate(raw)
  if (kind === 'datetime') {
    const d = parseDate(raw)
    if (!d) return null
    const time = String(raw).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/)
    return time
      ? `${d}T${time[1].padStart(2, '0')}:${time[2]}:${(time[3] ?? '00').padStart(2, '0')}+02:00`
      : `${d}T12:00:00+02:00`
  }
  const t = raw.trim()
  return t === '' ? null : t
}

export function ImportDialog({
  title,
  blurb,
  fields,
  busy,
  result,
  error,
  onCancel,
  onSubmit,
}: ImportDialogProps) {
  const [sheet, setSheet] = useState<Sheet | null>(null)
  const [fileName, setFileName] = useState('')
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [readError, setReadError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function onFile(file: File | undefined) {
    if (!file) return
    setReadError(null)
    try {
      const text = await file.text()
      const s = toSheet(text)
      if (s.headers.length === 0 || s.rows.length === 0) {
        setReadError(
          'No rows were found in that file. If it is an Excel workbook, export it as CSV first: this reads CSV only.',
        )
        return
      }
      setSheet(s)
      setFileName(file.name)
      const guess: Record<string, string> = {}
      for (const f of fields) guess[f.key] = guessColumn(s.headers, f.candidates)
      setMapping(guess)
    } catch {
      setReadError('That file could not be read as text. Export it as CSV and try again.')
    }
  }

  const mapped = useMemo(() => {
    if (!sheet) return []
    return sheet.rows.map((r) => {
      const o: Record<string, string | number | null> = {}
      for (const f of fields) {
        const col = mapping[f.key]
        o[f.key] = col ? coerce(f.kind, r[col] ?? '') : null
      }
      return o
    })
  }, [sheet, mapping, fields])

  const missingRequired = fields.filter((f) => f.required && !mapping[f.key])
  const unreadable = mapped.filter((r) =>
    fields.some((f) => f.required && (r[f.key] === null || r[f.key] === '')),
  )
  const usable = mapped.filter((r) => !fields.some((f) => f.required && (r[f.key] === null || r[f.key] === '')))

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-3xl rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{blurb}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {!sheet ? (
            <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-8 text-center">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Choose the CSV export.</p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <Button type="button" onClick={() => inputRef.current?.click()}>
                Choose a file
              </Button>
              {readError && (
                <p className="flex items-center gap-2 text-xs text-destructive">
                  <AlertCircle className="h-4 w-4" /> {readError}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{fileName}</span>
                <span>·</span>
                <span className="tabular-nums">{sheet.rows.length} rows</span>
                <button
                  type="button"
                  className="ml-auto underline"
                  onClick={() => {
                    setSheet(null)
                    setFileName('')
                  }}
                >
                  Choose a different file
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {fields.map((f) => (
                  <label key={f.key} className="block text-xs">
                    <span className="font-medium text-foreground">
                      {f.label}
                      {f.required && <span className="text-destructive"> *</span>}
                    </span>
                    <NativeSelect
                      className="mt-1 w-full"
                      value={mapping[f.key] ?? ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    >
                      <option value="">Not in this file</option>
                      {sheet.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </NativeSelect>
                    {f.help && <span className="mt-1 block text-muted-foreground">{f.help}</span>}
                  </label>
                ))}
              </div>

              <div className="rounded-md border border-border">
                <div className="border-b border-border px-3 py-2 text-xs font-medium text-foreground">
                  What will be sent, as it was read
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        {fields.map((f) => (
                          <th key={f.key} className="px-3 py-1.5 text-left font-medium">
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mapped.slice(0, 6).map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          {fields.map((f) => (
                            <td key={f.key} className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                              {r[f.key] === null || r[f.key] === '' ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                String(r[f.key])
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {missingRequired.length > 0 && (
                <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  Still to map: {missingRequired.map((f) => f.label).join(', ')}.
                </p>
              )}
              {unreadable.length > 0 && (
                <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {unreadable.length} of {mapped.length} rows are missing a required value once read
                  and will not be sent. Check the column mapping before continuing: a date read the
                  wrong way round looks exactly like this.
                </p>
              )}
              {error && (
                <p className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
                </p>
              )}
              {result != null && (
                <pre className="flex items-start gap-2 overflow-x-auto rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Close
          </Button>
          <Button
            type="button"
            disabled={!sheet || missingRequired.length > 0 || usable.length === 0 || busy === true}
            onClick={() => onSubmit(usable, fileName)}
          >
            {busy ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Load {usable.length} row{usable.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  )
}
